import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { inr } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bank } from "@phosphor-icons/react";

export default function BankPerformance() {
  const [rows, setRows] = useState(null);

  useEffect(() => { api.get("/dashboard/bank-performance").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  if (!rows) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading bank performance…</div>;

  const totalJobs = rows.reduce((s, r) => s + r.jobs, 0);
  const tatRows = rows.filter((r) => r.avg_tat_days != null);
  const avgTat = tatRows.length ? (tatRows.reduce((s, r) => s + r.avg_tat_days, 0) / tatRows.length).toFixed(1) : "—";

  return (
    <div className="space-y-6" data-testid="bank-performance-page">
      <PageHeader overline="Dashboard" title="Bank-wise Performance" subtitle="Which banking relationships drive volume, and which are slipping on turnaround" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Banks Engaged" value={rows.length} testid="stat-banks" />
        <Stat label="Total Jobs" value={totalJobs} testid="stat-jobs" />
        <Stat label="Avg TAT (days)" value={avgTat} tone="text-amber-600" testid="stat-tat" />
        <Stat label="Fee Revenue" value={inr(rows.reduce((s, r) => s + r.revenue, 0))} tone="text-accent" testid="stat-revenue" />
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Bank}>No valuation jobs recorded yet — bank performance will appear here once jobs are created.</EmptyState>
      ) : (
        <>
          <Card className="p-6 border-border">
            <h3 className="font-display text-xl font-semibold text-primary">Jobs by Bank</h3>
            <p className="text-xs text-muted-foreground mb-4">Volume of valuation assignments received</p>
            <ResponsiveContainer width="100%" height={Math.max(220, rows.length * 38)}>
              <BarChart data={rows} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid horizontal={false} stroke="#E2E8F0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="bank_name" width={150} tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "rgba(10,37,64,0.05)" }} />
                <Bar dataKey="jobs" fill="#0A2540" radius={[0, 4, 4, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="border-border overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wide">Bank</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Jobs</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Delivered</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Avg TAT</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Fee Revenue</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Invoiced</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.bank_name} className="hover:bg-muted/40" data-testid={`bank-row-${r.bank_name}`}>
                      <TableCell className="font-medium text-primary text-sm">{r.bank_name}</TableCell>
                      <TableCell className="text-right tabular text-sm">{r.jobs}</TableCell>
                      <TableCell className="text-right tabular text-sm">{r.completed}</TableCell>
                      <TableCell className={`text-right tabular text-sm ${r.avg_tat_days > 7 ? "text-destructive font-semibold" : ""}`}>
                        {r.avg_tat_days != null ? `${r.avg_tat_days} d` : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular text-sm">{inr(r.revenue)}</TableCell>
                      <TableCell className="text-right tabular text-sm text-muted-foreground">{inr(r.invoiced)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
